import {
  IconButton,
  Stack,
  Typography,
  Box,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SxProps } from "@mui/system";
import MetricsByMonthDialogue from "./MetricsByMonth/MetricsByMonth";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedMonthData,
  setSelectedMonthForecastContentSharedUid,
  setSelectedMetric,
  setSelectedMonth,
  setSelectedSite,
} from "../../../../../features/forecastersSlice";
import store from "../../../../../store";
import { Theme } from "@emotion/react";
import {
  setDisplayErrorSnackBar,
  setUserToken,
} from "../../../../../features/userSlice";
import { usePastForecast } from "../../../../../customHooks/usePastForecast";
import ForecastSkeleton from "../CreateNewForeCast/ForecastSkeleton";
import {
  formatPercentageNonRound,
  formatPercentageNonRoundUptoTwo,
} from "../../../../../utils/helpers";
import "./FTDinfo.css";
import { TableWithOffsetScrollbar } from "./TableWithOffsetScrollbar";
import ViewOnlyInfoCard from "../CreateNewForeCast/ViewOnlyInfoCard";
import { GROWTH_FIELDS } from "../CreateNewForeCast/helpers/GrowthConfig";
import SummaryRows from "./SummaryRows";
import { getGrowthColumnConfig } from "./TemplateConfig/growthColumnConfig";

interface Props {
  selectedForecaster: any;
  onTableReady?: () => void;
  showContent?: boolean;
}

const GrowthInfo = ({ selectedForecaster, onTableReady, showContent }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const monthNames = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    [],
  );
  const token = useSelector(setUserToken);
  const oauthTokenHeader = token?.payload?.user?.userToken;
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const selectedSiteFromRedux = useSelector(
    (state: any) => state.forecasters?.selectedSite,
  );
  const selectedSiteFromURL = searchParams.get("selectedSite");
  const selectedYear = searchParams.get("selectedYear") as any;
  const selectedSite = selectedSiteFromRedux || selectedSiteFromURL;
  const [openMetrics, setOpenMetrics] = useState(false);
  const [showOnlyForecasts, setShowOnlyForecasts] = useState(false);

  // State for selected cell (for ViewOnlyInfoCard)
  const [selectedCell, setSelectedCell] = useState<{
    fieldName: string;
    month: string;
  } | null>(null);

  const [selectedFieldInfo, setSelectedFieldInfo] = useState<{
    fieldType: string;
    valueSourceText: string;
    impactText: string;
  } | null>(null);

  // Mapping from table header labels to config field names
  const labelToConfigFieldName: Record<string, string> = {
    NRPU: "NRPU",
    FTD: "FTD",
    "Avg Net Rev To Active Users": "Average Net Revenue Per Active User",
    "CPA PV": "CPA PV",
    CVR: "Conversion %",
    Profit: "Profit",
    "Active Users": "Active Users",
    "Old Active Users": "Old Users",
    "Acquired Users": "Acquired Users",
    "Growth Target": "Growth",
    "Net Revenue": "Estimated Net Revenue",
    RS: "Estimated Revenue Share",
    "RS PV": "RS PV",
    "CPA Income": "Estimated CPA Income",
    "Total Income": "Total Income",
    Clicks: "Clicks",
    "EPC CPA": "EPC CPA",
    "EPC RS": "EPC RS",
    EPC: "EPC",
    CPC: "CPC",
    CAC: "CAC",
    Spend: "Spend",
    "Avg RS Percent": "Average RS %",
    "Churn Rate": "Churn Rate",
    "RS BR to NG Active Percent": "RS BR to NG Active Ratio",
    "Acquired to FTD": "Acquired Users / FTD Ratio",
  };

  // Derive the set of formula field-names for the currently selected cell.
  const highlightedFieldNames = useMemo(() => {
    if (!selectedCell) return new Set<string>();
    const configFieldName =
      labelToConfigFieldName[selectedCell.fieldName] || selectedCell.fieldName;
    const info = GROWTH_FIELDS[configFieldName];
    if (!info?.formulaFields) return new Set<string>();

    const aliases: Record<string, string> = {
      "growth target": "growth",
      "avg net rev to active users": "average net revenue per active user",
      "cpa income": "estimated cpa income",
      "net rev": "estimated net revenue",
      rs: "estimated revenue share",
      "rs pv": "rs pv",
    };

    const formulaFieldsSet = new Set<string>();
    info.formulaFields.split(",").forEach((s: string) => {
      const trimmed = s.trim().toLowerCase();
      formulaFieldsSet.add(trimmed);
      if (aliases[trimmed]) {
        formulaFieldsSet.add(aliases[trimmed]);
      }
    });

    return formulaFieldsSet;
    // eslint-disable-next-line
  }, [selectedCell]); // Removed labelToConfigFieldName dependency as it is constant in this file

  // Handler for cell click
  const handleCellClick = (fieldLabel: string, month: string) => {
    const configFieldName = labelToConfigFieldName[fieldLabel] || fieldLabel;
    setSelectedCell({ fieldName: fieldLabel, month });

    const fieldData = GROWTH_FIELDS[configFieldName];
    if (fieldData) {
      // Build the value text
      const valueSourceText =
        fieldData.type === "Formula"
          ? `The formula for ${configFieldName} is: ${fieldData.formula}` || ""
          : fieldData.glossaryText || "";

      // Build the impact text
      const impactText =
        fieldData.type === "Formula"
          ? fieldData.affectedFields
            ? `Changes in '${configFieldName}', you will affect: ${fieldData.affectedFields}`
            : `Changes in '${configFieldName}' will not affect other metrics directly.`
          : fieldData.affectedFields
            ? `By editing the '${configFieldName}', you will affect: ${fieldData.affectedFields}`
            : `Changes in '${configFieldName}' will not affect other metrics directly.`;

      setSelectedFieldInfo({
        fieldType: fieldData.type,
        valueSourceText,
        impactText,
      });
    } else {
      setSelectedFieldInfo(null);
    }
  };

  // Check if a cell is highlighted (affected by selection)
  const isHighlighted = (fieldName: string, month: string) => {
    if (!selectedCell || selectedCell.month !== month) return false;
    const configFieldName = labelToConfigFieldName[fieldName] || fieldName;
    return highlightedFieldNames.has(configFieldName.toLowerCase());
  };

  // Check if a cell is selected (clicked)
  const isSelected = (fieldName: string, month: string) => {
    return (
      selectedCell?.fieldName === fieldName && selectedCell?.month === month
    );
  };

  // Ensure 'page' param is '0' for this route
  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    if (
      location.pathname === "/business_leader/forecaster/forecasterInfo" &&
      newSearchParams.get("page") !== "0"
    ) {
      newSearchParams.set("page", "0");
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.pathname, navigate]);

  // Ensure selectedSite is set in Redux store when component mounts
  useEffect(() => {
    if (selectedSite && !selectedSiteFromRedux) {
      dispatch(setSelectedSite(selectedSite));
    }
  }, [selectedSite, selectedSiteFromRedux, dispatch]);

  const handleClickOpen = (metricKey: string) => {
    setOpenMetrics(true);
    dispatch(setSelectedMetric(metricKey));
    const currentMonth = new Date().getMonth(); // 0–11
    const month = String(currentMonth + 1).padStart(2, "0");
    const formattedMonth = `${selectedYear}-${month}-01`;
    dispatch(setSelectedMonth(formattedMonth));
    if (selectedSite) {
      dispatch(setSelectedSite(selectedSite));
    } else {
      store.dispatch(
        setDisplayErrorSnackBar(
          "Please select a site.No selected site found in the URL",
        ),
      );
    }
  };

  const handleClose = () => {
    setOpenMetrics(false);
  };

  const tableheaders = useMemo(
    () => [
      { id: 0, KEY: "month", label: "Month", metric: "month", sortable: true },
      { id: 1, KEY: "nrpu", label: "NRPU", metric: "nrpu", sortable: true },
      { id: 2, KEY: "ftd", label: "FTD", metric: "ftd", sortable: true },
      {
        id: 3,
        KEY: "avg_net_rev_per_active_user",
        label: "Avg Net Rev To Active Users",
        metric: "avg_net_rev_per_active_user",
        sortable: true,
      },
      {
        id: 4,
        KEY: "cpa_pv",
        label: "CPA PV",
        metric: "cpa_pv",
        sortable: true,
      },
      { id: 5, KEY: "cvr", label: "CVR", metric: "CVR", sortable: true },
      {
        id: 6,
        KEY: "profit",
        label: "Profit",
        metric: "profit",
        sortable: true,
      },
      {
        id: 7,
        KEY: "active_users",
        label: "Active Users",
        metric: "active_users",
        sortable: true,
      },
      {
        id: 8,
        KEY: "old_active_users",
        label: "Old Active Users",
        metric: "old",
        sortable: true,
      },
      {
        id: 9,
        KEY: "acquired_active_users",
        label: "Acquired Users",
        metric: "aquired",
        sortable: true,
      },
      {
        id: 10,
        KEY: "growth_target",
        label: "Growth Target",
        metric: "growth",
        sortable: true,
      },
      {
        id: 11,
        KEY: "net_revenue",
        label: "Net Revenue",
        metric: "net_rev",
        sortable: true,
      },
      {
        id: 12,
        KEY: "rs",
        label: "RS",
        metric: "rs_income_dollar",
        sortable: true,
      },
      { id: 13, KEY: "rs_pv", label: "RS PV", metric: "rs_pv", sortable: true },
      {
        id: 14,
        KEY: "cpa",
        label: "CPA Income",
        metric: "cpa_income_dollar",
        sortable: true,
      },
      {
        id: 15,
        KEY: "total_income",
        label: "Total Income",
        metric: "total_income",
        sortable: true,
      },
      {
        id: 16,
        KEY: "clicks",
        label: "Clicks",
        metric: "clicks",
        sortable: true,
      },
      {
        id: 17,
        KEY: "epc_cpa",
        label: "EPC CPA",
        metric: "EPC_CPA",
        sortable: true,
      },
      {
        id: 18,
        KEY: "epc_rs",
        label: "EPC RS",
        metric: "EPC_RS",
        sortable: true,
      },
      { id: 19, KEY: "epc", label: "EPC", metric: "EPC", sortable: true },
      { id: 20, KEY: "cpc", label: "CPC", metric: "CPC", sortable: true },
      { id: 21, KEY: "cac", label: "CAC", metric: "CAC", sortable: true },
      { id: 22, KEY: "spend", label: "Spend", metric: "spend", sortable: true },
      {
        id: 23,
        KEY: "avg_rs_deal_p",
        label: "Avg RS Percent",
        metric: "avg_rs_p",
        sortable: true,
      },
      {
        id: 24,
        KEY: "churn_rate",
        label: "Churn Rate",
        metric: "churn_rate",
        sortable: true,
      },
      {
        id: 25,
        KEY: "br_to_ng_active_rs_ratio",
        label: "RS BR to NG Active Percent",
        metric: "rs_br_ng",
        sortable: true,
      },
      {
        id: 26,
        KEY: "ratio_between_acquired_to_ftd",
        label: "Acquired to FTD",
        metric: "Ratio_between_acquired_to_ftd",
        sortable: true,
      },
    ],
    [],
  );

  const sortedRows = selectedForecaster?.forecaster_content || [];

  const isMonthPassed = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // If viewing past year's forecast, all months are considered passed
    if (year < currentYear) {
      return true;
    }

    // If viewing future year's forecast, no months are considered passed
    if (year > currentYear) {
      return false;
    }

    // For current year, compare with current date
    const monthEndDate = new Date(year, month, 0);
    return currentDate > monthEndDate;
  };

  //(A) for months that have passed
  //(R) for the first month of the current quarter (if not passed)
  //(F) for all other future months
  const getMonthIndicator = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // JS months are 0-based
    const currentDay = currentDate.getDate();

    // Case 1: If the month has already passed
    if (isMonthPassed(rowMonth)) {
      return "A";
    }

    // Case 2: If it's the current month
    if (year === currentYear && month === currentMonth) {
      return currentDay <= 15 ? "R" : "A"; // before or on 15th = R, after = A
    }

    // Case 3: Future months
    return "F";
  };

  const sortMonths = (rows: any[]) => {
    const monthOrder = {
      January: 1,
      February: 2,
      March: 3,
      April: 4,
      May: 5,
      June: 6,
      July: 7,
      August: 8,
      September: 9,
      October: 10,
      November: 11,
      December: 12,
    };

    return rows.sort((a, b) => {
      return (
        monthOrder[a.displayMonth as keyof typeof monthOrder] -
        monthOrder[b.displayMonth as keyof typeof monthOrder]
      );
    });
  };

  const modifiedRows = sortMonths(
    sortedRows.map((row: any) => {
      const [, month] = row.month.split("-").map(Number);
      const monthName = monthNames[month - 1];
      return { ...row, displayMonth: monthName };
    }),
  );

  const handleInfoIconClick = (row: any) => {
    dispatch(setSelectedMonthData(row));
    dispatch(setSelectedMonthForecastContentSharedUid(row?.content_shared_uid));
  };

  const formatNumber = (value: number | string) => {
    if (typeof value === "number") {
      return new Intl.NumberFormat().format(Math.round(value));
    }
    return value;
  };

  // when clicked display only the forecasted months (the months with 'F' indicator)
  const handleToggleDisplayForecasts = (event: any) => {
    setShowOnlyForecasts(event.target.checked);
    // Force recalc of sticky offsets (triggers your useLayoutEffect)
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 0);
  };

  const { forecastData, loading } = usePastForecast(
    modifiedRows,
    oauthTokenHeader,
    selectedSite,
  );

  const CleanedHistoryData = forecastData.map((data: any) => {
    const cleanedItems = Object.fromEntries(
      Object.entries(data).filter(
        ([key]) =>
          !["input_params", "meta_data", "output_metadata"].includes(key),
      ),
    );
    return cleanedItems;
  });

  const getDeviation = (month: string, metricName: string) => {
    //  Check if the month has passed — only then include it
    if (getMonthIndicator(month) !== "A") return null;

    //  Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData.find(
      (item: any) => item.month === month,
    );

    if (!monthData) return null;

    const metricItem = monthData.query_output.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase(),
    );

    return metricItem?.Deviation ?? null;
  };

  const getActual = (month: string, metricName: string) => {
    // Check if the month has passed — only then include it
    if (getMonthIndicator(month) !== "A") return null;

    // Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData?.find(
      (item: any) => item.month === month,
    );

    if (!monthData) return null;

    const metricItem = monthData.query_output.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase(),
    );

    return metricItem?.Actual ?? null;
  };

  const getQuarterData = (quarterEndIndex: number) => {
    const quarterStartIndex = quarterEndIndex - 2; // 3 months per quarter
    const quarterMonths = modifiedRows.slice(
      quarterStartIndex,
      quarterEndIndex + 1,
    );

    return {
      ftd: quarterMonths.reduce((sum, m) => sum + parseFloat(m.ftd || 0), 0),
      profit: quarterMonths.reduce(
        (sum, m) => sum + parseFloat(m.profit || 0),
        0,
      ),
      avgGrowth: quarterMonths.reduce(
        (sum, m) => sum + parseFloat(m.growth_target || 0),
        0,
      ),
      netRevenue: quarterMonths.reduce(
        (sum, m) => sum + parseFloat(m.net_revenue || 0),
        0,
      ),
      rs: quarterMonths.reduce((sum, m) => sum + parseFloat(m.rs || 0), 0),
      cpaIncome: quarterMonths.reduce(
        (sum, m) => sum + parseFloat(m.cpa || 0),
        0,
      ),
      totalIncome: quarterMonths.reduce(
        (sum, m) => sum + parseFloat(m.total_income || 0),
        0,
      ),
      spend: quarterMonths.reduce(
        (sum, m) => sum + parseFloat(m.spend || 0),
        0,
      ),
      CAC: quarterMonths.reduce((sum, m) => sum + parseFloat(m.cac || 0), 0),
      clicks: quarterMonths.reduce(
        (sum, m) => sum + parseFloat(m.clicks || 0),
        0,
      ),
    };
  };

  const getYearData = () => {
    return {
      ftd: modifiedRows.reduce((sum, m) => sum + parseFloat(m.ftd || 0), 0),
      profit: modifiedRows.reduce(
        (sum, m) => sum + parseFloat(m.profit || 0),
        0,
      ),
      avgGrowth: modifiedRows.reduce(
        (sum, m) => sum + parseFloat(m.growth_target || 0),
        0,
      ),
      netRevenue: modifiedRows.reduce(
        (sum, m) => sum + parseFloat(m.net_revenue || 0),
        0,
      ),
      rs: modifiedRows.reduce((sum, m) => sum + parseFloat(m.rs || 0), 0),
      cpaIncome: modifiedRows.reduce(
        (sum, m) => sum + parseFloat(m.cpa || 0),
        0,
      ),
      totalIncome: modifiedRows.reduce(
        (sum, m) => sum + parseFloat(m.total_income || 0),
        0,
      ),
      spend: modifiedRows.reduce((sum, m) => sum + parseFloat(m.spend || 0), 0),
      CAC: modifiedRows.reduce((sum, m) => sum + parseFloat(m.cac || 0), 0),
      clicks: modifiedRows.reduce(
        (sum, m) => sum + parseFloat(m.clicks || 0),
        0,
      ),
    };
  };

  // IDs of left and right sticky columnst5
  const leftStickyIds = useMemo(
    () => new Set<number>([0, 1, 2, 3, 4, 5, 6]),
    [],
  );
  const rightStickyIds = useMemo(() => new Set<number>([]), []);

  const headerCellRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  const assignedCountRef = useRef(0);
  // whenever header count changes, ensure the ref array has that length
  useEffect(() => {
    headerCellRefs.current = Array(tableheaders.length).fill(null);
    assignedCountRef.current = 0;
  }, [tableheaders.length]);

  // state to hold computed offsets for left and right sticky cells
  const [computedLeftOffsets, setComputedLeftOffsets] = useState<
    Record<number, number>
  >({});
  const [computedRightOffsets, setComputedRightOffsets] = useState<
    Record<number, number>
  >({});

  // callbackRef factory — used as ref prop on TableCell
  const makeHeaderRef =
    (index: number) => (el: HTMLTableCellElement | null) => {
      // assign element (can be null when unmounting)
      const prev = headerCellRefs.current[index];
      headerCellRefs.current[index] = el;

      // if element newly attached, increment assigned count
      if (!prev && el) {
        assignedCountRef.current++;
      }

      // if element removed, decrement (keeps count accurate on re-renders)
      if (prev && !el) {
        assignedCountRef.current = Math.max(0, assignedCountRef.current - 1);
      }
    };

  // shallow comparison helper — only compare own-number-valued props (fast)
  const offsetsEqual = (
    a: Record<number, number>,
    b: Record<number, number>,
  ) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if ((a as any)[k] !== (b as any)[k]) return false;
    }
    return true;
  };

  // measure widths and compute cumulative left/right offsets
  useLayoutEffect(() => {
    let mounted = true;
    let ro: ResizeObserver | null = null;

    const computeOffsets = () => {
      if (!mounted) return;
      const leftOffsets: Record<number, number> = {};
      let cumLeft = 0;
      for (let i = 0; i < tableheaders.length; i++) {
        if (leftStickyIds.has(i)) {
          leftOffsets[i] = cumLeft;
          const el = headerCellRefs.current[i];
          const w = el ? el.getBoundingClientRect().width : i === 0 ? 115 : 0;
          cumLeft += w;
        }
      }

      const rightOffsets: Record<number, number> = {};
      let cumRight = 0;
      const rightIds = Array.from(rightStickyIds).sort((a, b) => b - a);
      for (const id of rightIds) {
        rightOffsets[id] = cumRight;
        const el = headerCellRefs.current[id];
        const w = el ? el.getBoundingClientRect().width : 115;
        cumRight += w;
      }

      setComputedLeftOffsets((prev) => {
        if (!mounted) return prev;
        if (offsetsEqual(prev || {}, leftOffsets)) return prev;
        return leftOffsets;
      });

      setComputedRightOffsets((prev) => {
        if (!mounted) return prev;
        if (offsetsEqual(prev || {}, rightOffsets)) return prev;
        return rightOffsets;
      });
    };

    const scheduleFinalMeasure = () => {
      // prefer fonts.ready when available, then double-RaF to ensure layout is final
      if ((document as any).fonts && (document as any).fonts.ready) {
        (document as any).fonts.ready.then(() => {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => mounted && computeOffsets()),
          );
        });
      } else {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => mounted && computeOffsets()),
        );
      }
    };

    // If header refs aren't all assigned yet, wait for them.
    // Otherwise run immediate measurement (and schedule the stable re-measure).
    const tryInitialMeasure = () => {
      const assigned = assignedCountRef.current;
      if (assigned < tableheaders.length) {
        // not all refs attached yet — defer until they are attached.
        // Poll briefly until assignedCount matches the header count (fast and stops rapidly).
        const check = () => {
          if (!mounted) return;
          if (assignedCountRef.current >= tableheaders.length) {
            computeOffsets(); // one immediate compute
            scheduleFinalMeasure();
          } else {
            // check again next frame (small, bounded polling)
            requestAnimationFrame(check);
          }
        };
        requestAnimationFrame(check);
      } else {
        // all refs available right now
        computeOffsets();
        scheduleFinalMeasure();
      }
    };

    // Start: try to measure once refs are present
    tryInitialMeasure();

    // Keep ResizeObserver for future changes, schedule via rAF
    const tableEl = document.querySelector(".FTDForecasterTable table");
    if (tableEl) {
      ro = new ResizeObserver(() =>
        requestAnimationFrame(() => mounted && computeOffsets()),
      );
      ro.observe(tableEl);
    }
    window.addEventListener("resize", computeOffsets);

    return () => {
      mounted = false;
      if (ro) ro.disconnect();
      window.removeEventListener("resize", computeOffsets);
    };
  }, [tableheaders, leftStickyIds, rightStickyIds]);

  //for scrolling from 6th profit column
  const FIXED_LAST_INDEX = 6;

  const fixedColumnsWidth = useMemo(() => {
    const offset = computedLeftOffsets[FIXED_LAST_INDEX] ?? 0;
    const el = headerCellRefs.current[FIXED_LAST_INDEX];
    const width = el ? el.getBoundingClientRect().width : 0;
    return offset + width;
  }, [computedLeftOffsets]);

  const tableScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forecastData.length > 0 && onTableReady) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onTableReady();
        });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastData.length]);

  return (
    <>
      {!loading && forecastData.length > 0 ? (
        showContent ? (
        <Paper elevation={0} sx={{ borderRadius: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
              position: "absolute",
              top: 68,
              right: 0,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  color="secondary"
                  data-tracker="521"
                  checked={showOnlyForecasts}
                  onChange={handleToggleDisplayForecasts}
                />
              }
              label="Show only forecasts"
              labelPlacement="start"
            />
          </Box>
          <ViewOnlyInfoCard
            selectedFieldName={selectedCell?.fieldName}
            fieldType={
              selectedFieldInfo?.fieldType as
                | "User input"
                | "Parameter"
                | "Formula"
                | null
            }
            valueSourceText={selectedFieldInfo?.valueSourceText}
            impactText={selectedFieldInfo?.impactText}
          />
          <>
            <TableContainer className="FTDForecasterTable" ref={tableScrollRef}>
              <Table aria-label="collapsible table" size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{}}>
                    {tableheaders.map((header) => {
                      const getCellSx = (headerId: number): SxProps<Theme> => {
                        const base: SxProps<Theme> = {
                          color: "#000000",
                          padding:
                            headerId === 0
                              ? "0px 8px 0px 16px!important"
                              : "0px 8px !important",
                          boxSizing: "border-box",
                          backgroundColor: "#fff",
                          top: 0,
                          borderCollapse: "separate",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        };

                        if (leftStickyIds.has(headerId)) {
                          return {
                            ...base,
                            position: "sticky",
                            left: computedLeftOffsets[headerId] ?? 0,
                            zIndex: 99,
                            backgroundColor: "#ffffff",
                            borderRight:
                              headerId === 6 ? "1px solid #e0e0e0" : undefined,
                            color: headerId === 0 ? "#000000" : "#9C27B0",
                          };
                        }

                        if (rightStickyIds.has(headerId)) {
                          return {
                            ...base,
                            position: "sticky",
                            right: computedRightOffsets[headerId],
                            zIndex: 110,
                            backgroundColor: "#ffffff",
                            borderLeft:
                              headerId === 23 ? "1px solid #e0e0e0" : undefined,
                          };
                        }

                        return base;
                      };

                      return (
                        <TableCell
                          key={header.id}
                          align="left"
                          sx={getCellSx(header.id)}
                          ref={makeHeaderRef(header.id)}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-start"
                            spacing={1}
                          >
                            {header.label !== "Month" &&
                            header.label !== "More" ? (
                              <IconButton
                                color="secondary"
                                size="small"
                                data-tracker="131"
                                onClick={() =>
                                  handleClickOpen(`${header.metric}`)
                                }
                              >
                                <HistoryIcon
                                  sx={{ fontSize: "1rem" }}
                                  color="secondary"
                                />
                              </IconButton>
                            ) : null}
                            {
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  lineHeight: "24px",
                                  letterSpacing: "0.17px",
                                }}
                              >
                                {header.label}
                              </Typography>
                            }
                          </Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modifiedRows?.map((row: any, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        <TableRow
                          sx={{
                            backgroundColor: isMonthPassed(row.month)
                              ? "#F5F5F5"
                              : "#FFFFFF",
                            // "& > *": {
                            //   borderBottom:
                            //     "1px solid rgba(224, 224, 224, 1) !important",
                            // },
                            height: 35,
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              backgroundColor: "#FFFFFF",
                              padding: "0px 8px 0px 16px !important",
                              position: "sticky",
                              left: computedLeftOffsets[0] ?? 0,
                              zIndex: 2,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              {getMonthIndicator(row.month) === "A" ? (
                                <RouterLink
                                  to={`MonthlySummery?${(() => {
                                    const params = new URLSearchParams(
                                      searchParams,
                                    );
                                    params.set("page", "0");
                                    params.set(
                                      "contentSharedUid",
                                      row?.content_shared_uid,
                                    );
                                    params.set(
                                      "month",
                                      `${row?.displayMonth} ${selectedYear}`,
                                    );
                                    params.set("selectedMonth", row?.month);
                                    return params.toString();
                                  })()}`}
                                  onClick={() => handleInfoIconClick(row)}
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                >
                                  <Typography variant="body2" color="secondary">
                                    ({showOnlyForecasts ? "F" : "A"})
                                  </Typography>
                                  {row?.displayMonth ? (
                                    <Typography
                                      variant="body2"
                                      color="secondary"
                                      component="span"
                                      sx={{ textDecoration: "underline" }}
                                    >
                                      {row.displayMonth}
                                    </Typography>
                                  ) : (
                                    ""
                                  )}
                                </RouterLink>
                              ) : (
                                <>
                                  <Typography variant="body2">
                                    ({getMonthIndicator(row.month)})
                                  </Typography>
                                  {row?.displayMonth ? (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      {row.displayMonth}
                                    </Typography>
                                  ) : (
                                    ""
                                  )}
                                </>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("NRPU", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "NRPU",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#ffffff",
                              padding: "0px 8px !important",
                              position: "sticky",
                              left: computedLeftOffsets[1],
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected("NRPU", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.nrpu ? (
                                      `${formatNumber(parseFloat(row.nrpu))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          parseFloat(
                                            getActual(row.month, "nrpu"),
                                          ),
                                        )}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "nrpu") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.nrpu ? (
                                  `${Math.round(Number(row.nrpu))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("FTD", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "FTD",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#FFFFFF",
                              padding: "0px 8px !important",
                              position: "sticky",
                              left: computedLeftOffsets[2],
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected("FTD", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.ftd ? (
                                      `${formatNumber(parseFloat(row.ftd))}`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "ftd"),
                                        ) || 0}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "ftd") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.ftd ? (
                                  `${formatNumber(parseFloat(row.ftd))}`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick(
                                "Avg Net Rev To Active Users",
                                row.displayMonth,
                              )
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Avg Net Rev To Active Users",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#ffffff",
                              padding: "0px 8px !important",
                              position: "sticky",
                              left: computedLeftOffsets[3],
                              zIndex: 2,
                              width: "150px",
                              minWidth: "150px",
                              maxWidth: "150px",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              border: isSelected(
                                "Avg Net Rev To Active Users",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.avg_net_rev_per_active_user ? (
                                      `${Number(
                                        row.avg_net_rev_per_active_user,
                                      ).toFixed(2)}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {`${Number(
                                          getActual(
                                            row.month,
                                            "avg_net_rev_per_active_user",
                                          ),
                                        ).toFixed(2)}$`}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "avg_net_rev_per_active_user",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.avg_net_rev_per_active_user ? (
                                  `${Number(
                                    row.avg_net_rev_per_active_user,
                                  ).toFixed(2)}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("CPA PV", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "CPA PV",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#ffffff",
                              padding: "0px 8px !important",
                              position: "sticky",
                              left: computedLeftOffsets[4],
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected("CPA PV", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.cpa_pv ? (
                                      `${formatNumber(parseFloat(row.cpa_pv))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "cpa_pv"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "cpa_pv") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.cpa_pv ? (
                                  `${formatNumber(parseFloat(row.cpa_pv))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("CVR", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "CVR",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#ffffff",
                              padding: "0px 8px !important",
                              position: "sticky",
                              left: computedLeftOffsets[5],
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected("CVR", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.cvr ? (
                                      `${formatPercentageNonRound(row?.cvr)}`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatPercentageNonRound(
                                          getActual(row.month, "CVR"),
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "CVR") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.cvr ? (
                                  `${formatPercentageNonRound(row?.cvr)}`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Profit", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Profit",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#ffffff",
                              padding: "0px 8px !important",
                              position: "sticky",
                              left: computedLeftOffsets[6],
                              zIndex: 2,
                              borderRight: isSelected(
                                "Profit",
                                row.displayMonth,
                              )
                                ? undefined
                                : "1px solid #e0e0e0",
                              cursor: "pointer",
                              border: isSelected("Profit", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.profit ? (
                                      `${formatNumber(parseFloat(row.profit))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "profit"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "profit") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.profit ? (
                                  `${formatNumber(parseFloat(row.profit))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Active Users", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Active Users",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected(
                                "Active Users",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.active_users ? (
                                      `${formatNumber(
                                        parseFloat(row.active_users),
                                      )}`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "active_users"),
                                        ) || 0}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "active_users",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.active_users ? (
                                  `${formatNumber(parseFloat(row.active_users))}`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick(
                                "Old Active Users",
                                row.displayMonth,
                              )
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Old Active Users",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected(
                                "Old Active Users",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.old_active_users ? (
                                      `${formatNumber(
                                        parseFloat(row.old_active_users),
                                      )}`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(
                                            row.month,
                                            "old_active_users",
                                          ),
                                        ) || 0}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "old_active_users",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.old_active_users ? (
                                  `${formatNumber(
                                    parseFloat(row.old_active_users),
                                  )}`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick(
                                "Acquired Users",
                                row.displayMonth,
                              )
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Acquired Users",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected(
                                "Acquired Users",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.acquired_active_users ? (
                                      `${formatNumber(
                                        parseFloat(row.acquired_active_users),
                                      )}`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(
                                            row.month,
                                            "acquired_active_users",
                                          ),
                                        ) || 0}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "acquired_active_users",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.acquired_active_users ? (
                                  `${formatNumber(
                                    parseFloat(row.acquired_active_users),
                                  )}`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Growth Target", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Growth Target",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected(
                                "Growth Target",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.growth_target ? (
                                      `${formatPercentageNonRoundUptoTwo(
                                        row?.growth_target,
                                      )}`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatPercentageNonRoundUptoTwo(
                                          getActual(row.month, "growth"),
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "growth") *
                                          100
                                        ).toFixed(2)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.growth_target ? (
                                  `${formatPercentageNonRoundUptoTwo(
                                    row?.growth_target,
                                  )}`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Net Revenue", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Net Revenue",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected(
                                "Net Revenue",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.net_revenue ? (
                                      `${formatNumber(
                                        parseFloat(row.net_revenue),
                                      )}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "net_rev"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "net_rev") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.net_revenue ? (
                                  `${formatNumber(parseFloat(row.net_revenue))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("RS", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "RS",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("RS", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.rs ? (
                                      `${formatNumber(parseFloat(row.rs))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(
                                            row.month,
                                            "rs_income_dollar",
                                          ),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "rs_income_dollar",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.rs ? (
                                  `${formatNumber(parseFloat(row.rs))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("RS PV", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "RS PV",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("RS PV", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.rs_pv ? (
                                      `${formatNumber(parseFloat(row.rs_pv))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "rs_pv"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "rs_pv") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.rs_pv ? (
                                  `${formatNumber(parseFloat(row.rs_pv))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("CPA Income", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "CPA Income",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("CPA Income", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.cpa ? (
                                      `${formatNumber(parseFloat(row.cpa))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(
                                            row.month,
                                            "cpa_income_dollar",
                                          ),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "cpa_income_dollar",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.cpa ? (
                                  `${formatNumber(parseFloat(row.cpa))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Total Income", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Total Income",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected(
                                "Total Income",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.total_income ? (
                                      `${formatNumber(
                                        parseFloat(row.total_income),
                                      )}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "total_income"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "total_income",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.total_income ? (
                                  `${formatNumber(parseFloat(row.total_income))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Clicks", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Clicks",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("Clicks", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.clicks ? (
                                      `${formatNumber(parseFloat(row.clicks))}`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "clicks"),
                                        ) || 0}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "clicks") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.clicks ? (
                                  `${formatNumber(parseFloat(row.clicks))}`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("EPC CPA", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "EPC CPA",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("EPC CPA", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.epc_cpa ? (
                                      `${formatNumber(parseFloat(row.epc_cpa))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "EPC_CPA"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "EPC_CPA") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.epc_cpa ? (
                                  `${formatNumber(parseFloat(row.epc_cpa))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("EPC RS", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "EPC RS",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("EPC RS", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.epc_rs ? (
                                      `${formatNumber(parseFloat(row.epc_rs))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "EPC_RS"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "EPC_RS") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.epc_rs ? (
                                  `${formatNumber(parseFloat(row.epc_rs))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("EPC", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "EPC",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("EPC", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.epc ? (
                                      `${formatNumber(parseFloat(row.epc))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "EPC"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "EPC") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.epc ? (
                                  `${formatNumber(parseFloat(row.epc))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("CPC", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "CPC",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("CPC", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.cpc ? (
                                      `${formatNumber(parseFloat(row.cpc))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "CPC"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "CPC") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.cpc ? (
                                  `${formatNumber(parseFloat(row.cpc))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("CAC", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "CAC",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("CAC", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.cac ? (
                                      `${formatNumber(parseFloat(row.cac))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "CAC"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "CAC") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.cac ? (
                                  `${formatNumber(parseFloat(row.cac))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Spend", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Spend",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : "#F5F5F5",
                              padding: "0px 8px !important",
                              cursor: "pointer",
                              border: isSelected("Spend", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    row?.spend ? (
                                      `${formatNumber(parseFloat(row.spend))}$`
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatNumber(
                                          getActual(row.month, "spend"),
                                        ) || 0}
                                        $
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "spend") * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : row?.spend ? (
                                  `${formatNumber(parseFloat(row.spend))}$`
                                ) : (
                                  ""
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick(
                                "Avg RS Percent",
                                row.displayMonth,
                              )
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Avg RS Percent",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : getMonthIndicator(row.month) === "A"
                                  ? "#F5F5F5"
                                  : "#FFFFFF",
                              padding: "0px 8px !important",
                              right: computedRightOffsets[23],
                              position: "sticky",
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected(
                                "Avg RS Percent",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    formatPercentageNonRound(row?.avg_rs_deal_p)
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatPercentageNonRound(
                                          getActual(row.month, "avg_rs_p"),
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "avg_rs_p") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : (
                                  formatPercentageNonRound(row?.avg_rs_deal_p)
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick("Churn Rate", row.displayMonth)
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Churn Rate",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : getMonthIndicator(row.month) === "A"
                                  ? "#F5F5F5"
                                  : "#FFFFFF",
                              padding: "0px 8px !important",
                              right: computedRightOffsets[24],
                              position: "sticky",
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected("Churn Rate", row.displayMonth)
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    formatPercentageNonRound(row?.churn_rate)
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatPercentageNonRound(
                                          getActual(row.month, "churn_rate"),
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "churn_rate",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : (
                                  formatPercentageNonRound(row?.churn_rate)
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick(
                                "RS BR to NG Active Percent",
                                row.displayMonth,
                              )
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "RS BR to NG Active Percent",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : getMonthIndicator(row.month) === "A"
                                  ? "#F5F5F5"
                                  : "#FFFFFF",
                              padding: "0px 8px !important",
                              right: computedRightOffsets[25],
                              position: "sticky",
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected(
                                "RS BR to NG Active Percent",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    formatPercentageNonRound(
                                      row?.br_to_ng_active_rs_ratio,
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatPercentageNonRound(
                                          getActual(row.month, "rs_br_ng"),
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(row.month, "rs_br_ng") *
                                          100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : (
                                  formatPercentageNonRound(
                                    row?.br_to_ng_active_rs_ratio,
                                  )
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            align="center"
                            data-tracker="131"
                            onClick={() =>
                              handleCellClick(
                                "Acquired to FTD",
                                row.displayMonth,
                              )
                            }
                            sx={{
                              backgroundColor: isHighlighted(
                                "Acquired to FTD",
                                row.displayMonth,
                              )
                                ? "#F3E5F5"
                                : getMonthIndicator(row.month) === "A"
                                  ? "#F5F5F5"
                                  : "#FFFFFF",
                              padding: "0px 8px !important",
                              right: computedRightOffsets[26],
                              position: "sticky",
                              zIndex: 2,
                              cursor: "pointer",
                              border: isSelected(
                                "Acquired to FTD",
                                row.displayMonth,
                              )
                                ? "2px solid #9C27B0"
                                : undefined,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Typography variant="body2">
                                {getMonthIndicator(row.month) === "A" ? (
                                  showOnlyForecasts ? (
                                    formatPercentageNonRound(
                                      row?.ratio_between_acquired_to_ftd,
                                    )
                                  ) : (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {formatPercentageNonRound(
                                          getActual(
                                            row.month,
                                            "Ratio_between_acquired_to_ftd",
                                          ),
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="#00000099"
                                      >
                                        (
                                        {(
                                          getDeviation(
                                            row.month,
                                            "Ratio_between_acquired_to_ftd",
                                          ) * 100
                                        ).toFixed(1)}
                                        %)
                                      </Typography>
                                    </Stack>
                                  )
                                ) : (
                                  formatPercentageNonRound(
                                    row?.ratio_between_acquired_to_ftd,
                                  )
                                )}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <SummaryRows
                          row={row}
                          index={index}
                          modifiedRows={modifiedRows}
                          selectedYear={selectedYear}
                          computedLeftOffsets={computedLeftOffsets}
                          computedRightOffsets={computedRightOffsets}
                          getQuarterData={getQuarterData}
                          getYearData={getYearData}
                          columnMapping={getGrowthColumnConfig(
                            computedLeftOffsets,
                            computedRightOffsets,
                          )}
                        />
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              <Stack
                spacing={1}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              ></Stack>
            </TableContainer>
            <TableWithOffsetScrollbar
              fixedWidth={fixedColumnsWidth}
              scrollRef={tableScrollRef}
            />
          </>
          <MetricsByMonthDialogue
            open={openMetrics}
            handleClose={handleClose}
            selectedSiteFromParent={selectedSite}
          />
        </Paper>
        ) : (
          <ForecastSkeleton />
        )
      ) : (
        <ForecastSkeleton />
      )}
    </>
  );
};

export default GrowthInfo;
